/**
 * chunkRecovery — авто-восстановление после ChunkLoadError.
 *
 * После деплоя хеши чанков меняются. iOS-WebView Telegram игнорит
 * `no-store` и держит старый HTML, который тянет уже удалённый чанк
 * → `ChunkLoadError` на открытии. Лечим: ловим ошибку → чистим кэш →
 * один hard-reload с cache-bust. Свежий HTML приносит актуальные хеши.
 */

/** Чистит app-уровневый кэш: storage, service-workers, Cache API. */
export async function purge(): Promise<void> {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    /* private-mode storage может бросить — игнор */
  }
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  if (typeof caches !== "undefined") {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

/** True, если ошибка — провал загрузки чанка (вкл. WebKit-формулировку). */
export function isChunkError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string };
  if (e.name === "ChunkLoadError") return true;
  const msg = e.message ?? String(err);
  return /loading chunk|failed to load chunk|chunkloaderror|importing a module script failed/i.test(
    msg,
  );
}

const GUARD_KEY = "cg-chunk-recovery-at";
const COOLDOWN_MS = 12_000;

/**
 * Чистит кэш и форсит один hard-reload с cache-bust.
 * Anti-loop: повторный вызов в пределах COOLDOWN — no-op (флаг читаем
 * ДО purge, ставим ПОСЛЕ, т.к. purge чистит sessionStorage).
 */
export async function recoverOnce(): Promise<void> {
  if (typeof window === "undefined") return;
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(GUARD_KEY) || 0);
  } catch {
    /* игнор */
  }
  const now = Date.now();
  if (now - last < COOLDOWN_MS) return;
  await purge();
  try {
    sessionStorage.setItem(GUARD_KEY, String(now));
  } catch {
    /* игнор */
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(now));
  window.location.replace(url.toString());
}
